import { z } from 'zod';
/** Fixed task status enum — see docs/PLAN.md §3. Order matters for board columns. */
export const TASK_STATUSES = [
    'backlog',
    'in_progress',
    'on_hold',
    'blocked',
    'testing',
    'in_review',
    'done',
    'canceled',
];
export const TaskStatusSchema = z.enum(TASK_STATUSES);
export const TASK_PRIORITIES = ['critical', 'high', 'medium', 'low'];
export const TaskPrioritySchema = z.enum(TASK_PRIORITIES);
/** Org roles, highest privilege first. `guest` is for external stakeholders (e.g. a client):
 * they see only the projects they're explicitly added to, can't create projects, can't see the
 * org's member directory, and are capped at GUEST_MAX_PROJECT_ROLE on any project. */
export const ORG_ROLES = ['owner', 'admin', 'member', 'guest'];
export const OrgRoleSchema = z.enum(ORG_ROLES);
/** Lifecycle of an org invite — see schemas/org.ts OrgInviteSchema. */
export const ORG_INVITE_STATUSES = ['pending', 'accepted', 'revoked'];
export const OrgInviteStatusSchema = z.enum(ORG_INVITE_STATUSES);
/** Project roles, highest privilege first. */
export const PROJECT_ROLES = ['owner', 'lead', 'member', 'viewer'];
export const ProjectRoleSchema = z.enum(PROJECT_ROLES);
export const DOC_TYPES = ['prd', 'tdd'];
export const DocTypeSchema = z.enum(DOC_TYPES);
export const DOCUMENT_SOURCES = ['manual', 'llm'];
export const DocumentSourceSchema = z.enum(DOCUMENT_SOURCES);
export const ISSUE_SEVERITIES = ['low', 'medium', 'high'];
export const IssueSeveritySchema = z.enum(ISSUE_SEVERITIES);
export const ISSUE_STATUSES = ['open', 'resolved'];
export const IssueStatusSchema = z.enum(ISSUE_STATUSES);
/** Comment tags mirror a subset of task statuses used conversationally. */
export const COMMENT_STATUS_TAGS = ['on_hold', 'blocked', 'in_review', 'testing'];
export const CommentStatusTagSchema = z.enum(COMMENT_STATUS_TAGS);
export const DELIVERABLE_KINDS = [
    'screenshot',
    'client_folder',
    'deployment',
    'cloud_project',
    'link',
    'file',
    /** A training session's slide deck — kept in step with the deck's current version, never
     * duplicated per version (see services/training.ts upsertSessionDeliverable). */
    'slides',
];
export const DeliverableKindSchema = z.enum(DELIVERABLE_KINDS);
/** The kinds an AI agent may add through the MCP connector — everything but 'slides', which only
 * the training pipeline creates. */
export const AGENT_DELIVERABLE_KINDS = [
    'screenshot',
    'client_folder',
    'deployment',
    'cloud_project',
    'link',
    'file',
];
export const REGENERATION_PROPOSAL_KINDS = ['affected', 'new', 'obsolete'];
export const RegenerationProposalKindSchema = z.enum(REGENERATION_PROPOSAL_KINDS);
export const REGENERATION_PROPOSAL_DECISIONS = ['pending', 'accepted', 'rejected'];
export const RegenerationProposalDecisionSchema = z.enum(REGENERATION_PROPOSAL_DECISIONS);
export const EXPORT_FORMATS = ['markdown', 'pdf'];
export const ExportFormatSchema = z.enum(EXPORT_FORMATS);
/** Where an audited action came from. 'mcp' is an AI coding agent acting through the MCP
 * connector on a user's behalf (apps/api/src/mcp) — kept distinct from 'ui' precisely so an
 * activity feed can answer "did a human do this, or did their agent?" without guessing. */
export const AUDIT_SOURCES = ['ui', 'cli', 'system', 'mcp'];
export const AuditSourceSchema = z.enum(AUDIT_SOURCES);
export const JOB_TYPES = [
    'prd_extract',
    'prd_brainstorm',
    'tdd_questionnaire',
    'tdd_generate',
    'tasks_generate',
    'tasks_import',
    'regeneration',
    'progress_summary',
    'project_ask',
    'discussion_catchup',
    'training_analyze',
    'training_sessions',
    'training_outline',
    'training_slides',
    'training_impact',
    'training_export',
];
export const JobTypeSchema = z.enum(JOB_TYPES);
export const JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed'];
export const JobStatusSchema = z.enum(JOB_STATUSES);
/** What kind of engagement a project is. Drives which categories the TDD questionnaire asks
 * about (see questionnairePrompt in apps/api/src/llm/prompts.ts) and which DEV_PREFERENCE_FIELDS
 * a project's org-level defaults are relevant for (see DEV_PREFERENCE_FIELD_META below). An org
 * is not itself typed — it can run projects of several types at once, so this lives on Project,
 * not Org. */
export const PROJECT_TYPES = [
    'web_dev',
    'cloud_migration',
    'security_scan',
    'training',
    'other',
];
export const ProjectTypeSchema = z.enum(PROJECT_TYPES);
export const PROJECT_TYPE_LABEL = {
    web_dev: 'Web development',
    cloud_migration: 'Cloud migration',
    security_scan: 'Security scan',
    training: 'Training',
    other: 'Other',
};
/** Display label for a project's type — the user's own `otherProjectTypeLabel` when the type is
 * 'other', so a custom engagement is described by what it actually is rather than the generic
 * "Other" fallback, everywhere the type is shown (UI chips, the questionnaire prompt, etc). */
export function projectTypeLabel(projectType, otherProjectTypeLabel) {
    if (projectType === 'other' && otherProjectTypeLabel)
        return otherProjectTypeLabel;
    return PROJECT_TYPE_LABEL[projectType];
}
/** The single-value stack categories in DevPreferencesSchema (schemas/org.ts) — kept as its own
 * const so a TDD questionnaire question can tag which one it answers (see
 * QuestionnaireQuestionSchema.devPreferenceField in schemas/tdd.ts), letting the UI offer
 * "save as org default" on exactly the questions that map to a real preference field. Excludes
 * `conventions`, which is free-form prose rather than something a single question answers. */
export const DEV_PREFERENCE_FIELDS = [
    'frontend',
    'styling',
    'backend',
    'architectureStyle',
    'datastore',
    'auth',
    'hosting',
    'cicd',
    'testing',
    'observability',
    'thirdPartyServices',
    'sourceEnvironment',
    'targetCloudProvider',
    'complianceTargets',
    'attackSurface',
];
export const DevPreferenceFieldSchema = z.enum(DEV_PREFERENCE_FIELDS);
/** Human label, tooltip description, and applicable project type(s) for each dev preference
 * field — a `Record` keyed by `DevPreferenceField` so adding a field above without an entry here
 * is a compile error. `appliesTo` is what lets the questionnaire prompt ask only about categories
 * that matter for a given project's type, and what the org "Dev preferences" page's search/filter
 * is keyed on. Many fields apply to more than one type (e.g. `hosting` matters for both a web
 * build and a cloud migration). `description` is shown as a tooltip next to the field's label on
 * that same page. */
export const DEV_PREFERENCE_FIELD_META = {
    frontend: {
        label: 'Frontend stack',
        description: 'The framework and language the client/UI is built with, e.g. React, Next.js, SvelteKit.',
        appliesTo: ['web_dev'],
    },
    styling: {
        label: 'Styling',
        description: 'How the UI is styled, e.g. Tailwind CSS, CSS Modules, styled-components.',
        appliesTo: ['web_dev'],
    },
    backend: {
        label: 'Backend runtime',
        description: 'Where server-side code runs, e.g. Express on Cloud Run, Cloud Functions, Fastify.',
        appliesTo: ['web_dev', 'cloud_migration'],
    },
    architectureStyle: {
        label: 'Architecture style',
        description: 'The overall shape of the system, e.g. modular monolith, microservices, serverless/event-driven.',
        appliesTo: ['web_dev', 'cloud_migration'],
    },
    datastore: {
        label: 'Datastore',
        description: 'The primary place data is persisted, e.g. Firestore, PostgreSQL (Cloud SQL), MongoDB.',
        appliesTo: ['web_dev', 'cloud_migration'],
    },
    auth: {
        label: 'Authentication',
        description: 'How users prove who they are, e.g. Firebase Auth (Google sign-in, email link), custom OIDC.',
        appliesTo: ['web_dev', 'security_scan'],
    },
    hosting: {
        label: 'Hosting & deployment target',
        description: 'Where the app actually runs once deployed, e.g. Firebase Hosting, Vercel, Cloudflare Pages.',
        appliesTo: ['web_dev', 'cloud_migration'],
    },
    cicd: {
        label: 'CI/CD',
        description: 'How code gets from a commit to a live deployment, e.g. GitHub Actions, Cloud Build triggers.',
        appliesTo: ['web_dev', 'cloud_migration'],
    },
    testing: {
        label: 'Testing strategy',
        description: 'How correctness is verified before shipping, e.g. Vitest + Playwright, Jest + Cypress.',
        appliesTo: ['web_dev'],
    },
    observability: {
        label: 'Observability',
        description: 'How the running system is monitored in production, e.g. Cloud Logging + Error Reporting, Sentry.',
        appliesTo: ['web_dev', 'cloud_migration', 'security_scan'],
    },
    thirdPartyServices: {
        label: 'Third-party services',
        description: 'External services the project depends on, e.g. Stripe, SendGrid, a payments or email provider.',
        appliesTo: ['web_dev', 'cloud_migration', 'security_scan'],
    },
    sourceEnvironment: {
        label: 'Source environment',
        description: 'Where the workload currently runs, before the migration — e.g. on-prem, AWS, another GCP project.',
        appliesTo: ['cloud_migration'],
    },
    targetCloudProvider: {
        label: 'Target cloud provider',
        description: 'The cloud provider the workload is being migrated to, e.g. Google Cloud, AWS, Azure.',
        appliesTo: ['cloud_migration'],
    },
    complianceTargets: {
        label: 'Compliance targets',
        description: 'Regulatory or industry standards the scan needs to check against, e.g. SOC 2, PCI DSS, HIPAA.',
        appliesTo: ['security_scan'],
    },
    attackSurface: {
        label: 'Attack surface / scope',
        description: 'What is in scope for the scan, e.g. the public web app only, the internal network, or both.',
        appliesTo: ['security_scan'],
    },
};
/** No field is tagged 'other' — an untyped/custom engagement's real shape isn't known ahead of
 * time, so every category is offered and the questionnaire prompt's own "skip what's clearly
 * irrelevant" instruction (informed by the project's own otherProjectTypeLabel) does the
 * narrowing instead. */
export function devPreferenceFieldsForProjectType(projectType) {
    if (projectType === 'other')
        return [...DEV_PREFERENCE_FIELDS];
    return DEV_PREFERENCE_FIELDS.filter((field) => DEV_PREFERENCE_FIELD_META[field].appliesTo.includes(projectType));
}
/** Ordered role rank helpers — used by RBAC middleware to compare "at least this role". */
export const orgRoleRank = { owner: 4, admin: 3, member: 2, guest: 1 };
export const projectRoleRank = {
    owner: 4,
    lead: 3,
    member: 2,
    viewer: 1,
};
export function hasAtLeastOrgRole(role, minimum) {
    return orgRoleRank[role] >= orgRoleRank[minimum];
}
export function hasAtLeastProjectRole(role, minimum) {
    return projectRoleRank[role] >= projectRoleRank[minimum];
}
/** Fields on a task that the LLM (via regeneration) is allowed to touch. Everything else —
 * status, assigneeUid, actuals, comments, issues, attachments — is off-limits by construction.
 * See docs/PLAN.md §6. */
export const REGENERATION_OWNED_TASK_FIELDS = [
    'title',
    'description',
    'acceptanceCriteria',
    'priority',
    'estimateHours',
    'dependsOn',
    'trace',
];
/** A person's availability, set by hand from the sidebar (there's no automatic presence
 * detection). Shown as a dot next to their name everywhere, and logged with timestamps per org
 * for the Team activity page — see schemas/userStatus.ts. */
export const USER_STATUSES = ['online', 'busy', 'offline'];
export const UserStatusSchema = z.enum(USER_STATUSES);
/** The status of someone who has never set one. */
export const DEFAULT_USER_STATUS = 'offline';
//# sourceMappingURL=enums.js.map